import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { GLASS } from './glass';

// Homepage Science section: explains HOW Sleepstack evaluates evidence, not
// what is in the formula. The ingredient browser lives above ("5 functionele
// ingrediënten"); the full literature review lives on the standalone
// /science page. This section sits between them and answers "what is our
// research standard?".
// ---------------------------------------------------------------------------

const PRINCIPLES = [
    {
        num: '01',
        title_en: 'Human studies first',
        title_nl: 'Humane studies eerst',
        body_en: 'Wherever possible, we base decisions on research in people, not just mechanistic or animal studies.',
        body_nl: 'Waar mogelijk baseren we keuzes op onderzoek bij mensen, niet alleen op mechanistische of dierstudies.',
    },
    {
        num: '02',
        title_en: 'Dosage in context',
        title_nl: 'Dosering in context',
        body_en: 'A number on its own says little. We also look at the form used and, for plant extracts, how they\'re standardised.',
        body_nl: 'Een hoeveelheid zegt niet alles. We kijken ook naar de gebruikte vorm en, bij plantenextracten, naar standaardisatie.',
    },
    {
        num: '03',
        title_en: 'Outcomes, not promises',
        title_nl: 'Uitkomsten, geen beloftes',
        body_en: 'We show what researchers measured, without turning it into a guaranteed result.',
        body_nl: 'We laten zien wat onderzoekers hebben gemeten zonder daar een gegarandeerd effect van te maken.',
    },
];

const LABELS = {
    nl: {
        eyebrow: 'Onderzoek',
        heading: 'Onderzoek, zonder overdreven claims.',
        body: 'We kiezen niet eerst een ingrediënt en zoeken daarna een studie. We kijken naar humaan onderzoek, dosering, vorm en standaardisatie, en naar wat er daadwerkelijk is gemeten.',
        proof_eyebrow: 'Een concreet voorbeeld',
        proof_title: '3000 mg glycine',
        col_sleepstack: 'Sleepstack-dosis',
        col_studied: 'Onderzocht',
        studied_note: '3 g in humane slaapstudies',
        refs_label: 'Referenties',
        cta: 'Bekijk de onderbouwing van alle ingrediënten',
    },
    en: {
        eyebrow: 'Research',
        heading: 'Research, minus the hype.',
        body: 'We don’t pick an ingredient and then go looking for a study to back it up. We look at human research, dosage, form and standardisation, and at what was actually measured.',
        proof_eyebrow: 'Case in point',
        proof_title: '3000 mg glycine',
        col_sleepstack: 'Sleepstack dose',
        col_studied: 'In research',
        studied_note: '3 g in human sleep studies',
        refs_label: 'References',
        cta: 'See the evidence behind every ingredient',
    },
};

const REFERENCES = ['Bannai et al. 2012', 'Yamadera et al. 2007'];

export function ScienceTeaserSection() {
    const { lang } = useLanguage();
    const labels = LABELS[lang] || LABELS.en;

    return (
        <section className="section-padding relative" id="science" style={{ scrollMarginTop: '120px' }}>
            <div className="section-container">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className={`relative mt-10 mb-6 md:mb-8 max-w-2xl p-6 sm:p-8 lg:p-10 ${GLASS}`}
                >
                    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-primary absolute -top-8 left-1">
                        <span className="w-8 h-px bg-primary" />
                        {labels.eyebrow}
                    </div>
                    <h2 className="heading-lg text-foreground mb-3">{labels.heading}</h2>
                    <p className="text-base text-muted-foreground leading-relaxed">{labels.body}</p>
                </motion.div>

                {/* Three research principles */}
                <ol
                    className={`grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 p-6 sm:p-8 lg:p-10 ${GLASS}`}
                    data-testid="science-principles"
                >
                    {PRINCIPLES.map((p, i) => (
                        <motion.li
                            key={p.num}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.08 }}
                            className="md:border-l md:border-border/70 md:pl-8 md:first:border-l-0 md:first:pl-0 border-b md:border-b-0 border-border/70 pb-6 md:pb-0 last:border-b-0 last:pb-0"
                            data-testid={`science-principle-${p.num}`}
                        >
                            <div className="flex items-baseline gap-2.5 mb-3">
                                <span className="font-condensed font-extrabold text-primary text-3xl leading-none tracking-tight">
                                    {p.num}
                                </span>
                                <span className="font-condensed font-bold uppercase text-[11px] tracking-[0.18em] text-secondary-dark leading-none">
                                    {lang === 'nl' ? p.title_nl : p.title_en}
                                </span>
                            </div>
                            <p className="text-[14.5px] text-slate-700 leading-relaxed">
                                {lang === 'nl' ? p.body_nl : p.body_en}
                            </p>
                        </motion.li>
                    ))}
                </ol>

                {/* Proof example: glycine */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className={`mt-6 md:mt-8 p-6 sm:p-8 md:p-10 ${GLASS}`}
                    data-testid="science-proof-example"
                >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between md:gap-10">
                        <div className="md:max-w-xs mb-6 md:mb-0">
                            <div className="font-condensed font-bold uppercase text-[11px] tracking-[0.2em] text-primary mb-3">
                                {labels.proof_eyebrow}
                            </div>
                            <div className="font-condensed font-extrabold uppercase text-primary text-3xl md:text-4xl leading-none tracking-tight">
                                3000 MG
                            </div>
                            <div className="font-condensed font-bold uppercase text-secondary-dark text-lg md:text-xl mt-1 leading-none tracking-tight">
                                Glycine
                            </div>
                        </div>

                        <div className="flex-1 grid grid-cols-2 gap-4 sm:gap-6 md:max-w-md">
                            <div className="border-t-2 border-primary/70 pt-3">
                                <div className="font-condensed font-bold uppercase text-[10px] tracking-[0.18em] text-primary mb-1.5">
                                    {labels.col_sleepstack}
                                </div>
                                <div className="font-condensed font-extrabold text-foreground text-3xl md:text-4xl leading-none tabular-nums">
                                    3 g
                                </div>
                            </div>
                            <div className="border-t-2 border-slate-300 pt-3">
                                <div className="font-condensed font-bold uppercase text-[10px] tracking-[0.18em] text-slate-500 mb-1.5">
                                    {labels.col_studied}
                                </div>
                                <div className="font-condensed font-extrabold text-foreground text-3xl md:text-4xl leading-none tabular-nums">
                                    3 g
                                </div>
                                <div className="text-[11px] text-slate-500 mt-1.5 leading-snug">
                                    {labels.studied_note}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 md:mt-8 pt-5 border-t border-border/70">
                        <div className="font-condensed font-bold uppercase text-[10px] tracking-[0.18em] text-slate-500 mb-2">
                            {labels.refs_label}
                        </div>
                        <ul className="flex flex-wrap gap-x-5 gap-y-1.5" data-testid="science-proof-refs">
                            {REFERENCES.map((r) => (
                                <li key={r} className="text-sm text-slate-700 tabular-nums">
                                    {r}
                                </li>
                            ))}
                        </ul>
                    </div>
                </motion.div>

                {/* CTA */}
                <div className="mt-8 md:mt-10">
                    <Link
                        to="/science"
                        className="inline-flex items-center gap-2 text-primary font-semibold text-sm hover:underline"
                        data-testid="science-cta-link"
                    >
                        {labels.cta}
                        <ArrowRight size={14} />
                    </Link>
                </div>
            </div>
        </section>
    );
}
